import {
  AssetCheck,
  AssetStatus,
  EmailTemplateContent,
  ValidationFinding,
} from "./emailTemplateValidation.types"
import {
  checkFooterYear,
  extractAssetUrls,
  extractLinkUrls,
  extractVariables,
  findExtensionArtifacts,
  findScriptFindings,
  isAbsoluteAssetUrl,
  isInlineAssetUrl,
  isAllowedAssetUrl,
} from "./emailTemplateValidationRules"

const PROBE_TIMEOUT_MS = 15000

interface ImageProbe {
  status: AssetStatus
  width: number | null
  height: number | null
}

// Load the asset as an image: this works cross-origin (unlike fetch) and
// yields both availability and pixel dimensions.
const probeImage = (url: string): Promise<ImageProbe> =>
  new Promise((resolve) => {
    const image = new Image()
    const timer = setTimeout(
      () => resolve({ status: "unverified", width: null, height: null }),
      PROBE_TIMEOUT_MS,
    )
    image.onload = () => {
      clearTimeout(timer)
      resolve({
        status: "available",
        width: image.naturalWidth,
        height: image.naturalHeight,
      })
    }
    image.onerror = () => {
      clearTimeout(timer)
      resolve({ status: "unreachable", width: null, height: null })
    }
    image.src = url
  })

export interface IEmailTemplateValidationService {
  extractVariables(htmlContent: string, plainContent: string): string[]
  extractLinks(htmlContent: string, plainContent: string): string[]
  runStaticChecks(content: EmailTemplateContent): ValidationFinding[]
  checkAssets(content: EmailTemplateContent): Promise<AssetCheck[]>
  assetFindings(assets: AssetCheck[]): ValidationFinding[]
}

class EmailTemplateValidationServiceClass implements IEmailTemplateValidationService {
  extractVariables(htmlContent: string, plainContent: string): string[] {
    return extractVariables(htmlContent, plainContent)
  }

  extractLinks(htmlContent: string, plainContent: string): string[] {
    return extractLinkUrls(htmlContent, plainContent)
  }

  runStaticChecks({ htmlContent }: EmailTemplateContent): ValidationFinding[] {
    return [
      ...findScriptFindings(htmlContent),
      ...findExtensionArtifacts(htmlContent),
      ...checkFooterYear(htmlContent),
    ]
  }

  // Actionable problems derived from probed assets, for the combined issues
  // modal. Unverified assets are informational only (shown in the asset list).
  assetFindings(assets: AssetCheck[]): ValidationFinding[] {
    return assets.flatMap((asset) => {
      const findings: ValidationFinding[] = []
      if (asset.inline) {
        findings.push({
          rule: "asset-origin",
          severity: "warning",
          message: "Asset is embedded in the email and some clients block it",
          detail: asset.url,
        })
      } else if (!asset.absolute) {
        findings.push({
          rule: "asset-origin",
          severity: "error",
          message:
            "Asset URL is not absolute and will not load once the email is sent",
          detail: asset.url,
        })
      } else if (!asset.allowed) {
        findings.push({
          rule: "asset-origin",
          severity: "error",
          message: "Asset is not served from an approved So Energy asset host",
          detail: asset.url,
        })
      }
      if (asset.status === "unreachable") {
        findings.push({
          rule: "asset-availability",
          severity: "error",
          message: "Asset appears to be unreachable",
          detail: asset.url,
        })
      }
      return findings
    })
  }

  async checkAssets({
    htmlContent,
  }: EmailTemplateContent): Promise<AssetCheck[]> {
    return Promise.all(
      extractAssetUrls(htmlContent).map(async (url) => {
        const absolute = isAbsoluteAssetUrl(url)
        const inline = isInlineAssetUrl(url)
        // Probing a relative URL would resolve it against Nova's own origin,
        // which says nothing about the asset an email client would request.
        const probe = absolute
          ? await probeImage(url)
          : { status: "unverified" as const, width: null, height: null }
        return {
          url,
          absolute,
          inline,
          allowed: absolute && isAllowedAssetUrl(url),
          status: probe.status,
          width: probe.width,
          height: probe.height,
        }
      }),
    )
  }
}

export const EmailTemplateValidationService =
  new EmailTemplateValidationServiceClass()
